/* __next_internal_client_entry_do_not_use__ INamedImage,ImageCarousel,ImageContainer auto */ import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { AspectRatioPicture } from "@faire/retailer-visitor-shared/components/Image/AspectRatioPicture";
import { CarouselDots } from "@faire/retailer-visitor-shared/components/ImageCarousel/CarouselDots";
import { getAltTag } from "@faire/retailer-visitor-shared/features/Marketplace/utils";
import { isTouchDevice } from "@faire/web--source/common/isTouchDevice";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { TelemetryManager } from "@faire/web--source/common/opentelemetry/TelemetryManager";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { optimizeProductImageUrl } from "@faire/web--source/common/transform-images";
import { Chevron } from "@faire/web--source/slate/icons/foundation/Chevron";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
export var INamedImage;
(function(INamedImage) {
    INamedImage.build = function(partial) {
        return _object_spread_props(_object_spread({}, IImage.build(partial)), {
            name: partial === null || partial === void 0 ? void 0 : partial.name
        });
    };
})(INamedImage || (INamedImage = {}));
var ImageCarouselBase = function(param) {
    var images = param.images, ImageWrapper = param.ImageWrapper, withBorder = param.withBorder, pageType = param.pageType, name = param.name, title = param.title, children = param.children, hideDotsWhenSingleImage = param.hideDotsWhenSingleImage, onImageSelected = param.onImageSelected, otelSpanName = param.otelSpanName, onImageScrolled = param.onImageScrolled;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var isSSR = useIsSSR();
    // Set images array to a ref to save re-rendering if the reference to the array changes
    var imagesRef = useRef(images);
    imagesRef.current = images;
    var onImageSelectedRef = useRef(onImageSelected);
    onImageSelectedRef.current = onImageSelected;
    var imageSelectActionRef = useRef(undefined);
    // Don't enable the carousel if there is only one image
    var shouldRenderCarousel = imagesRef.current.length > 1;
    // Only render the first image
    var _useState = _sliced_to_array(useState(false), 2), shouldRenderAll = _useState[0], setShouldRenderAll = _useState[1];
    var showNavigationButtons = !isTouchDevice() && shouldRenderCarousel;
    var _useState1 = _sliced_to_array(useState(0), 2), selectedIndex = _useState1[0], setSelectedIndex = _useState1[1];
    var slideInnerContainerRef = useRef(null);
    var refCallback = useCallback(function(node) {
        if (node) {
            slideInnerContainerRef.current = node;
            var onScroll = function() {
                var scrollLeft = node.scrollLeft;
                var clientWidth = node.clientWidth;
                var index = Math.round(scrollLeft / clientWidth);
                setSelectedIndex(index);
                setShouldRenderAll(true);
            };
            slideInnerContainerRef.current.addEventListener("scroll", onScroll);
        }
    }, []);
    var scrollToIndex = useCallback(function(requestedIndex) {
        if (slideInnerContainerRef.current) {
            var _slideInnerContainerRef_current_scrollTo, _slideInnerContainerRef_current;
            var index = requestedIndex;
            var maxIndex = imagesRef.current.length - 1;
            if (index < 0) {
                index = maxIndex;
            } else if (index > maxIndex) {
                index = 0;
            }
            (_slideInnerContainerRef_current_scrollTo = (_slideInnerContainerRef_current = slideInnerContainerRef.current).scrollTo) === null || _slideInnerContainerRef_current_scrollTo === void 0 ? void 0 : _slideInnerContainerRef_current_scrollTo.call(_slideInnerContainerRef_current, {
                left: index * slideInnerContainerRef.current.clientWidth,
                behavior: "smooth"
            });
        }
    }, []);
    var scrollPrev = useCallback(function() {
        imageSelectActionRef.current = "leftNav";
        scrollToIndex(selectedIndex - 1);
        onImageScrolled === null || onImageScrolled === void 0 ? void 0 : onImageScrolled(selectedIndex, "leftNav");
    }, [
        onImageScrolled,
        scrollToIndex,
        selectedIndex
    ]);
    var scrollNext = useCallback(function() {
        imageSelectActionRef.current = "rightNav";
        scrollToIndex(selectedIndex + 1);
        onImageScrolled === null || onImageScrolled === void 0 ? void 0 : onImageScrolled(selectedIndex, "rightNav");
    }, [
        onImageScrolled,
        scrollToIndex,
        selectedIndex
    ]);
    var scrollTo = useCallback(function(index) {
        imageSelectActionRef.current = "dot";
        scrollToIndex(index);
        onImageScrolled === null || onImageScrolled === void 0 ? void 0 : onImageScrolled(index, "dot");
    }, [
        onImageScrolled,
        scrollToIndex
    ]);
    useEffect(function() {
        if (imageSelectActionRef.current) {
            var _onImageSelectedRef_current;
            (_onImageSelectedRef_current = onImageSelectedRef.current) === null || _onImageSelectedRef_current === void 0 ? void 0 : _onImageSelectedRef_current.call(onImageSelectedRef, selectedIndex, imageSelectActionRef.current);
        }
        imageSelectActionRef.current = "swipe";
    }, [
        selectedIndex
    ]);
    var ImageWrapperComponent = ImageWrapper !== null && ImageWrapper !== void 0 ? ImageWrapper : React.Fragment;
    return /*#__PURE__*/ _jsxs(Container, {
        children: [
            /*#__PURE__*/ _jsx(ImageWrapperComponent, {
                children: /*#__PURE__*/ _jsxs(SliderContainer, {
                    children: [
                        /*#__PURE__*/ _jsx(SliderInnerContainer, {
                            ref: refCallback,
                            children: imagesRef.current.map(function(image, index) {
                                var imageUrl = optimizeProductImageUrl(image, isSSR);
                                var span = undefined;
                                if (otelSpanName) {
                                    span = TelemetryManager.get().startManagedSpan(otelSpanName);
                                    if (!imageUrl && span) {
                                        TelemetryManager.get().endManagedSpan(span);
                                    }
                                }
                                var _image_name, _getAltTag, _ref, _image_token;
                                return /*#__PURE__*/ _jsx(ImageContainer, {
                                    $withBorder: withBorder,
                                    children: shouldRenderAll || index === 0 ? /*#__PURE__*/ _jsx(AspectRatioPicture, {
                                        $width: "100%",
                                        $ratio: 1,
                                        $hideBorder: true,
                                        children: /*#__PURE__*/ _jsx(Image, {
                                            alt: (_ref = (_getAltTag = getAltTag({
                                                productName: (_image_name = image.name) !== null && _image_name !== void 0 ? _image_name : name,
                                                pageType: pageType,
                                                title: title
                                            }, strictLocalize)) !== null && _getAltTag !== void 0 ? _getAltTag : image.name) !== null && _ref !== void 0 ? _ref : name,
                                            src: imageUrl,
                                            onLoad: function() {
                                                if (span) {
                                                    TelemetryManager.get().endManagedSpan(span);
                                                }
                                            }
                                        })
                                    }) : null
                                }, (_image_token = image.token) !== null && _image_token !== void 0 ? _image_token : index);
                            })
                        }),
                        /*#__PURE__*/ _jsx(ImageOverlayContainer, {
                            children: children
                        })
                    ]
                })
            }),
            showNavigationButtons ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(ChevronContainer, {
                        $position: "left",
                        onClick: scrollPrev,
                        role: "button",
                        "aria-label": strictLocalize({
                            id: "dmlNx/",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Previous Carousel Slide"
                                }
                            ]
                        }),
                        children: /*#__PURE__*/ _jsx(Chevron, {
                            direction: "left",
                            strokeWidth: 2
                        })
                    }),
                    /*#__PURE__*/ _jsx(ChevronContainer, {
                        $position: "right",
                        onClick: scrollNext,
                        role: "button",
                        "aria-label": strictLocalize({
                            id: "kHyFNJ",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Next Carousel Slide"
                                }
                            ]
                        }),
                        children: /*#__PURE__*/ _jsx(Chevron, {
                            direction: "right",
                            strokeWidth: 2
                        })
                    })
                ]
            }) : null,
            /*#__PURE__*/ _jsx(Spacer, {
                height: "1x"
            }),
            hideDotsWhenSingleImage && !shouldRenderCarousel ? null : /*#__PURE__*/ _jsx(CarouselDots, {
                onClick: function(index) {
                    scrollTo(index);
                },
                currentImageIndex: selectedIndex,
                numValidImages: imagesRef.current.length
            })
        ]
    });
};
export var ImageCarousel = observer(ImageCarouselBase);
var ChevronContainer = styled(Row).attrs({
    align: "center",
    justify: "center"
}).withConfig({
    componentId: "sc-75341cb2-0"
})([
    "width:32px;height:32px;top:42%;",
    ";position:absolute;cursor:pointer;z-index:2;opacity:0;transition:0.5s;"
], function(param) {
    var position = param.$position;
    return position === "left" ? "left: 0" : "right: 0";
});
var Container = styled(Column).withConfig({
    componentId: "sc-75341cb2-1"
})([
    "flex-shrink:0;position:relative;overflow:hidden;user-select:none;-moz-user-select:none;-khtml-user-select:none;-webkit-user-select:none;-o-user-select:none;&:hover{",
    "{opacity:1;background-color:rgba(255,255,255,0.75);}}"
], ChevronContainer);
var SliderContainer = styled.div.withConfig({
    componentId: "sc-75341cb2-2"
})([
    "overflow:hidden;position:relative;"
]);
var SliderInnerContainer = styled.div.withConfig({
    componentId: "sc-75341cb2-3"
})([
    "display:flex;overflow-x:auto;scroll-snap-type:x mandatory;-webkit-overflow-scrolling:touch;scrollbar-width:none;-ms-overflow-style:none;&::-webkit-scrollbar{display:none;}"
]);
var ImageOverlayContainer = styled.div.withConfig({
    componentId: "sc-75341cb2-4"
})([
    "top:0;bottom:0;left:0;right:0;z-index:1;position:absolute;pointer-events:none;"
]);
export var ImageContainer = styled.div.withConfig({
    componentId: "sc-75341cb2-5"
})([
    "position:relative;scroll-snap-align:center;scroll-snap-stop:always;min-width:0;background:",
    ";",
    " flex:0 0 100%;"
], Core.surface.muted, function(props) {
    return props.$withBorder ? css([
        "border:1px solid ",
        ";"
    ], Core.border.muted) : css([
        ""
    ]);
});
var Image = styled.img.withConfig({
    componentId: "sc-75341cb2-6"
})([
    "display:block;top:0;width:100%;height:100%;object-fit:cover;object-position:center;"
]);
